import { cva, type VariantProps } from "class-variance-authority";
import { CircleAlert, CircleCheck, Info, Lightbulb, TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

const alertVariants = cva("flex gap-3 rounded-control border px-3.5 py-3 text-sm", {
  variants: {
    tone: {
      info: "border-info/25 bg-info-soft text-info-ink",
      success: "border-success/25 bg-success-soft text-success-ink",
      warning: "border-warning/30 bg-warning-soft text-warning-ink",
      danger: "border-danger/30 bg-danger-soft text-danger-ink",
      neutral: "border-line bg-surface-2 text-fg-soft",
      /** Site `.real-life` callout: gold left border, used for tips / "why this matters". */
      tip: "rounded-l-none border-0 border-l-[3px] border-l-gold bg-warning-soft text-warning-ink",
    },
  },
  defaultVariants: { tone: "info" },
});

const ICONS = { info: Info, success: CircleCheck, warning: TriangleAlert, danger: CircleAlert, neutral: Info, tip: Lightbulb } as const;

export interface AlertProps extends VariantProps<typeof alertVariants> {
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
  icon?: ReactNode | false;
}

/** Inline callout. Not colour-only: always an icon + text (WCAG 1.4.1). */
export function Alert({ tone = "info", title, children, action, className, icon }: AlertProps) {
  const Icon = ICONS[tone ?? "info"];
  return (
    <div role={tone === "danger" ? "alert" : "status"} className={cn(alertVariants({ tone }), className)}>
      {icon !== false && <span className="mt-0.5 shrink-0 [&_svg]:size-4">{icon ?? <Icon aria-hidden />}</span>}
      <div className="min-w-0 flex-1 space-y-0.5">
        {title && <p className={cn("font-bold", tone === "tip" && "kicker")}>{title}</p>}
        {children && <div className="leading-relaxed [&_a]:font-bold [&_a]:underline">{children}</div>}
      </div>
      {action && <div className="shrink-0 self-center">{action}</div>}
    </div>
  );
}
